import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import type { IExtendedSearchItem } from "src/api/client/extended-search.api.types";
import { SEARCH_TABLE_FIRST_COLUMN_WIDTH, SEARCH_TABLE_NAME_COLUMN_ID } from "src/components/pages/search/components/table/consts";
import { ISearchTableFigure } from "src/components/pages/search/components/table/search-table.types";
import { TableScrollableCell } from "src/components-mui/base/table-scrollable/components/cell/table-scrollable-cell";
import { TableScrollableHeaderCell } from "src/components-mui/base/table-scrollable/components/cell/table-scrollable-header-cell";
import { TableTraderEntityWithTags } from "src/components-mui/base/table-scrollable/components/entity/table-trader-entity-with-tags";
import { TableRankingValue } from "src/components-mui/base/table-scrollable/components/table-ranking-value";
import { Typography } from "src/components-mui/base/typography/typography";
import { WatchlistButton } from "src/components-mui/common/buttons/watchlist-button/watchlist-button";
import { mapTraderToAvatarUser } from "src/components-mui/common/trader-avatar/trader-avatar.mappers";
import { HeaderCellTitleLabel } from "src/components-mui/modules/wikifolio-table/components/header-cell-title-label";
import { figureSortingFn } from "src/components-mui/modules/wikifolio-table/helpers/sorting";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const makeSearchTableColumns = (figures?: ISearchTableFigure[], hasAnyTags = false): ColumnDef<IExtendedSearchItem, any>[] => {
    const columnHelper = createColumnHelper<IExtendedSearchItem>();

    return [
        columnHelper.accessor(row => mapTraderToAvatarUser({ trader: row.trader }), {
            id: SEARCH_TABLE_NAME_COLUMN_ID,
            enableSorting: false,
            header: ({ header }) => (
                <TableScrollableHeaderCell width={SEARCH_TABLE_FIRST_COLUMN_WIDTH} header={header} shrinkable>
                    <HeaderCellTitleLabel />
                </TableScrollableHeaderCell>
            ),
            cell: ({ getValue, row }) => {
                const trader = getValue();
                return (
                    <TableScrollableCell
                        width={SEARCH_TABLE_FIRST_COLUMN_WIDTH}
                        shrinkable
                        contentSx={{ justifyContent: ["space-between", "space-between", "initial"], paddingRight: [1, 1, 0] }}
                    >
                        <TableTraderEntityWithTags
                            wikifolioShortDescription={row.original.wikifolioShortDescription}
                            wikifolioSymbol={row.original.symbol}
                            trader={trader}
                            tags={row.original.tags}
                            showTagsColumn={hasAnyTags}
                            openLinksInNewTab
                            avatarLoading="eager"
                        />
                    </TableScrollableCell>
                );
            },
        }),
        ...(figures?.map(figure => {
            const isSortingEnabled = figure.orderDescending !== null;
            return columnHelper.accessor(row => row.figures?.find(r => r.id === figure.id), {
                id: figure.id,
                sortingFn: figureSortingFn,
                enableSorting: isSortingEnabled,
                sortDescFirst: figure.orderDescending ?? false,
                header: ({ header }) => (
                    <TableScrollableHeaderCell header={header} tooltip={figure.tooltip?.content}>
                        <Typography
                            dangerouslySetInnerHTML={{ __html: figure.fullLabel }}
                            sx={{
                                fontWeight: "bold",
                                fontSize: "xs",
                            }}
                        />
                    </TableScrollableHeaderCell>
                ),
                cell: ({ getValue }) => (
                    <TableScrollableCell>
                        <TableRankingValue ranking={getValue()} />
                    </TableScrollableCell>
                ),
            });
        }) || []),

        // Custom display column — no backing data field; renders the watchlist toggle
        columnHelper.display({
            id: "follow",
            enableSorting: false,
            header: () => <TableScrollableHeaderCell sx={{ minWidth: "unset", width: "72px", paddingX: [1, 1, 1, 2] }}></TableScrollableHeaderCell>,
            cell: ({ row }) => (
                <TableScrollableCell sx={{ minWidth: "unset", width: "72px", paddingX: [1, 1, 1, 2] }}>
                    <WatchlistButton
                        isOnWatchlist={row.original.isOnWatchlist}
                        wikifolioId={row.original.wikifolioId}
                        wikifolioShortDescription={row.original.wikifolioShortDescription}
                        omitTitle
                        size="small"
                    />
                </TableScrollableCell>
            ),
        }),
    ];
};
